import { useState } from 'react'
import { Outlet, NavLink, useNavigate } from 'react-router-dom'
import { useAuth } from '@/contexts/AuthContext'
import { SelectedSchoolProvider } from '@/contexts/SelectedSchoolContext'
import { Icon, ICONS, type IconName } from '@/components/ui/Icon'
import { useInstructorCalendarBadge, useStudentCalendarBadge, useStudentCourseBadge } from '@/hooks/useNavCounts'
import type { UserPrimaryRole } from '@/types'

interface NavItem { label: string; to: string; icon: IconName }

const SCHOOL_NAV: NavItem[] = [
  { label: 'Overview',        to: '/app/school',                  icon: 'grid' },
  { label: 'Employees',       to: '/app/school/instructors',      icon: 'users' },
  { label: 'Contractors',     to: '/app/school/contractors',      icon: 'dollar' },
  { label: 'Fleet',           to: '/app/school/fleet',            icon: 'car' },
  { label: 'Students',        to: '/app/school/students',         icon: 'users' },
  { label: 'Tracking Sheets', to: '/app/school/tracking-sheets',  icon: 'checkSquare' },
  { label: 'Calendar',        to: '/app/school/calendar',         icon: 'calendar' },
  { label: 'Reports',         to: '/app/school/reports',          icon: 'barChart' },
  { label: 'Settings',        to: '/app/school/settings',         icon: 'gear' },
]
const INSTRUCTOR_NAV: NavItem[] = [
  { label: 'Dashboard',   to: '/app/instructor',          icon: 'grid' },
  { label: 'Students',    to: '/app/instructor/students', icon: 'users' },
  { label: 'Calendar',    to: '/app/instructor/calendar', icon: 'calendar' },
  { label: 'Schools',     to: '/app/instructor/schools',  icon: 'building' },
  { label: 'Library',     to: '/app/instructor/library',  icon: 'book' },
  { label: 'Mock Test',   to: '/app/instructor/mock',     icon: 'pencil' },
  { label: 'Settings',    to: '/app/instructor/settings', icon: 'gear' },
]
const STUDENT_NAV: NavItem[] = [
  { label: 'Home',        to: '/app/student',             icon: 'grid' },
  { label: 'Course',      to: '/app/student/course',      icon: 'book' },
  { label: 'My Instructor', to: '/app/student/instructor', icon: 'users' },
  { label: 'Calendar',    to: '/app/student/calendar',    icon: 'calendar' },
  { label: 'Checklist',   to: '/app/student/checklist',   icon: 'checkSquare' },
  { label: 'Mock Test',   to: '/app/student/mock',        icon: 'pencil' },
  { label: 'Certificate', to: '/app/student/certificate', icon: 'star' },
  { label: 'Settings',    to: '/app/student/settings',    icon: 'gear' },
]
// 'Admins' is filtered out below unless profile.is_superadmin — only a superadmin may
// see or use that menu (the route itself is also guarded, in App.tsx).
const ADMIN_NAV: NavItem[] = [
  { label: 'Schools',     to: '/app/admin',                icon: 'building' },
  { label: 'Students',    to: '/app/admin/students',       icon: 'users' },
  { label: 'Instructors', to: '/app/admin/instructors',    icon: 'users' },
  { label: 'E-learning',  to: '/app/admin/e-learning',     icon: 'book' },
  { label: 'Admins',      to: '/app/admin/admins',         icon: 'star' },
]

const NAV_MAP: Record<UserPrimaryRole, NavItem[]> = {
  school_admin: SCHOOL_NAV,
  instructor:   INSTRUCTOR_NAV,
  student:      STUDENT_NAV,
  drivetrack_admin: ADMIN_NAV,
}

function NavBadge({ count }: { count: number }) {
  if (count <= 0) return null
  return (
    <span style={{
      marginLeft: 'auto', background: '#0F7C6C', color: '#fff', borderRadius: 20,
      minWidth: 18, height: 18, padding: '0 5px', flexShrink: 0,
      font: '700 11px Inter, sans-serif', display: 'flex', alignItems: 'center', justifyContent: 'center',
    }}>
      {count > 99 ? '99+' : count}
    </span>
  )
}

interface AppShellProps { role: UserPrimaryRole }

export default function AppShell({ role }: AppShellProps) {
  const navigate = useNavigate()
  const { profile, signOut } = useAuth()
  const [mobOpen, setMobOpen] = useState(false)

  const navItems = (role === 'drivetrack_admin' ? NAV_MAP[role].filter(item => item.label !== 'Admins' || profile?.is_superadmin) : NAV_MAP[role])

  // Sidebar badge counts — "something here needs your attention." Hooks run
  // unconditionally (rules of hooks); each one no-ops to 0 for a role/user it doesn't
  // apply to, so the small extra query for an irrelevant role is harmless.
  const instructorCalendarBadge = useInstructorCalendarBadge()
  const studentCalendarBadge = useStudentCalendarBadge()
  const studentCourseBadge = useStudentCourseBadge()

  const badgeForItem = (item: NavItem): number => {
    if (item.label === 'Calendar') return role === 'instructor' ? instructorCalendarBadge : role === 'student' ? studentCalendarBadge : 0
    if (item.label === 'Course' && role === 'student') return studentCourseBadge
    return 0
  }

  const handleSignOut = async () => {
    await signOut()
    navigate(role === 'drivetrack_admin' ? '/admin/login' : '/login', { replace: true })
  }

  const initials = profile?.full_name
    ? profile.full_name.split(' ').map(n => n[0]).join('').slice(0, 2).toUpperCase()
    : '?'

  return (
    <div className='app-wrap'>
      {/* Desktop sidebar */}
      <aside className='app-sidebar'>
        <div style={{ display: 'flex', alignItems: 'center', gap: 9, marginBottom: 32 }}>
          <img src='/logo-icon.png' alt='' height={36} style={{ display: 'block', flexShrink: 0 }} />
          <span style={{ font: '700 18px/1 Lora,serif', color: '#fff', letterSpacing: '-0.2px' }}>DriveTrack</span>
        </div>

        <nav style={{ flex: 1, display: 'flex', flexDirection: 'column', gap: 4 }}>
          {navItems.map(item => (
            <NavLink
              key={item.to}
              to={item.to}
              end={item.to === navItems[0].to}
              style={({ isActive }) => ({
                display: 'flex', alignItems: 'center', gap: 11,
                padding: '9px 12px', borderRadius: 9,
                font: '500 13.5px Inter, sans-serif',
                color: isActive ? '#fff' : 'rgba(255,255,255,0.55)',
                background: isActive ? 'rgba(255,255,255,0.12)' : 'transparent',
                textDecoration: 'none', transition: 'all 0.12s',
              })}
            >
              <span style={{ width: 18, display: 'flex', justifyContent: 'center', color: 'inherit' }}>
                <Icon path={ICONS[item.icon]} size={15} />
              </span>
              {item.label}
              <NavBadge count={badgeForItem(item)} />
            </NavLink>
          ))}
        </nav>

        {/* User / sign out */}
        <div style={{ borderTop: '1px solid rgba(255,255,255,0.1)', paddingTop: 16 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 10 }}>
            <div style={{
              width: 32, height: 32, borderRadius: '50%', background: 'rgba(15,124,108,0.5)',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              font: '600 12px Inter, sans-serif', color: '#fff', flexShrink: 0,
            }}>
              {initials}
            </div>
            <div style={{ overflow: 'hidden' }}>
              <div style={{ font: '600 12.5px Inter, sans-serif', color: '#fff', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                {profile?.full_name ?? 'Loading…'}
              </div>
              <div style={{ font: '400 11px Inter, sans-serif', color: 'rgba(255,255,255,0.45)', marginTop: 1 }}>
                {role.replace('_', ' ')}
              </div>
            </div>
          </div>
          <button
            onClick={handleSignOut}
            style={{
              width: '100%', padding: '8px 12px', background: 'rgba(255,255,255,0.07)',
              border: 'none', borderRadius: 8, font: '500 12.5px Inter, sans-serif',
              color: 'rgba(255,255,255,0.6)', cursor: 'pointer', textAlign: 'left',
            }}
          >
            Sign out
          </button>
        </div>
      </aside>

      {/* Mobile topbar */}
      <div className='app-mob-topbar' style={{ padding: '14px 16px', alignItems: 'center', justifyContent: 'space-between', position: 'sticky', top: 0, zIndex: 50 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <img src='/logo-icon.png' alt='' height={28} style={{ display: 'block', flexShrink: 0 }} />
          <span style={{ font: '700 16px/1 Lora,serif', color: '#fff' }}>DriveTrack</span>
        </div>
        <button
          onClick={() => setMobOpen(o => !o)}
          aria-label={mobOpen ? 'Close menu' : 'Open menu'}
          aria-expanded={mobOpen}
          style={{ background: 'none', border: 'none', color: '#fff', padding: 4, display: 'flex' }}
        >
          <Icon path={mobOpen ? ICONS.close : ICONS.menu} size={20} />
        </button>

        {/* Mobile nav drawer */}
        {mobOpen && (
          <div style={{
            position: 'fixed', inset: 0, top: 52, background: 'rgba(27,42,74,0.97)',
            zIndex: 100, padding: '16px 16px', display: 'flex', flexDirection: 'column', gap: 4,
          }}>
            {navItems.map(item => (
              <NavLink
                key={item.to}
                to={item.to}
                end={item.to === navItems[0].to}
                onClick={() => setMobOpen(false)}
                style={({ isActive }) => ({
                  display: 'flex', alignItems: 'center', gap: 11, padding: '11px 14px', borderRadius: 9,
                  font: '500 14px Inter, sans-serif',
                  color: isActive ? '#fff' : 'rgba(255,255,255,0.6)',
                  background: isActive ? 'rgba(255,255,255,0.12)' : 'transparent',
                  textDecoration: 'none',
                })}
              >
                <span style={{ width: 20, display: 'flex', justifyContent: 'center', color: 'inherit' }}>
                  <Icon path={ICONS[item.icon]} size={16} />
                </span>
                {item.label}
                <NavBadge count={badgeForItem(item)} />
              </NavLink>
            ))}
            <button
              onClick={handleSignOut}
              style={{
                marginTop: 8, padding: '11px 14px', background: 'rgba(255,255,255,0.07)',
                border: 'none', borderRadius: 9, font: '500 14px Inter, sans-serif',
                color: 'rgba(255,255,255,0.6)', cursor: 'pointer', textAlign: 'left',
              }}
            >
              Sign out
            </button>
          </div>
        )}
      </div>

      {/* Main content */}
      <main className='app-content'>
        {role === 'school_admin' ? (
          <SelectedSchoolProvider>
            <Outlet />
          </SelectedSchoolProvider>
        ) : (
          <Outlet />
        )}
      </main>
    </div>
  )
}
